/*
 * Copyright 2016 Google Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use strict';
import { APP_DATA } from './data.js';
import { MARKER_TYPE_NORTH, MARKER_TYPE_PHOTOSPHERE, MARKER_TYPE_PRODUCT, MARKER_TYPE_PROMO } from "./properties";
import * as Marzipano from "./vendor/marzipano";
import { logData } from "../../../app/crossconcern/helpers/generic/generic.helper";
import {LANG_CODE_EN, LANG_CODE_FR, LANG_CODE_IT} from "../../../app/crossconcern/utilities/properties/base.property";
import { LOCAL_STORAGE_CURRENT_LANG } from "../../../app/crossconcern/utilities/properties/localstorage.property";
import {ProductModel} from "../../../app/datastore/entities/product.model";

let panoElement;
let sceneNameElement;
let sceneListElement;
let sceneListContainer;
let sceneElements;
let sceneListToggleElement;
let fullscreenToggleElement;
let receivedScenes = [];
let startMousePosition;
let editMode = false;
let clickAlreadyEmitted = false;

let onMarkerEdit;
let onInfoMarkerOpen;
let onSphereClick;
let onMediaRequest;
let onSceneChanged;
let onCreateProductMarker;
let onCreatePromotionMarker;

let viewer;

let clickedMarkerYaw;

const limiter = Marzipano.RectilinearView.limit.traditional(2048, 120 * Math.PI / 180);
const view = new Marzipano.RectilinearView({yaw: Math.PI}, limiter);

// Viewer options.
const viewerOpts = {
    controls: {
        mouseViewMode: APP_DATA.settings.mouseViewMode
    }
};

export function start(scenes, sphereCallback, startSphereUUID) {
    const bowser = window.bowser;
    const screenfull = window.screenfull;

    // Grab elements from DOM.
    panoElement = document.querySelector('#pano');
    sceneNameElement = document.querySelector('#title-bar .scene-name');
    sceneListElement = document.querySelector('#scene-list');
    sceneListContainer = document.querySelector("#scene-list .scenes");
    sceneElements = document.querySelectorAll('#scene-list .scene');
    sceneListToggleElement = document.querySelector('#scene-list-toggle');
    fullscreenToggleElement = document.querySelector('#fullscreen-toggle');

    // Detect desktop or mobile mode.
    if (window.matchMedia) {
        var setMode = function() {
            if (mql.matches) {
                document.body.classList.remove('desktop');
                document.body.classList.add('mobile');
            } else {
                document.body.classList.remove('mobile');
                document.body.classList.add('desktop');
            }
        };
        var mql = matchMedia("(max-width: 500px), (max-height: 500px)");
        setMode();
        mql.addListener(setMode);
    } else {
        document.body.classList.add('desktop');
    }

    // Detect whether we are on a touch device.
    document.body.classList.add('no-touch');
    window.addEventListener('touchstart', function() {
        document.body.classList.remove('no-touch');
        document.body.classList.add('touch');
    });

    // Use tooltip fallback mode on IE < 11.
    if (bowser.msie && parseFloat(bowser.version) < 11) {
        document.body.classList.add('tooltip-fallback');
    }

    // Initialize viewer.
    viewer = new Marzipano.Viewer(panoElement, viewerOpts);

    // Get photospheres from backend
    // Every sphere is added to receivedScenes, added objects have the current attributes:
    // uuid:        string
    // name:        string
    // yaw:         number
    // media:       string
    // nurseries: Marzipano Scene Object
    // markers: [
    //  {
    //    uuid:               string
    //    markerType:         string
    //    yaw:                number
    //    pitch:              number
    //    distance:           number
    //    linkedPhotosphere:  string
    //    enabled:            boolean
    //    hotspot:            Marzipano Hotspot Object
    //  }
    // ]

    receivedScenes = scenes;
    onSphereClick = sphereCallback;

    // Display the initial scene.
    if(startSphereUUID) {
        changeSphereById(startSphereUUID)
    } else if(receivedScenes) {
        switchScene(receivedScenes[0], null);
    }

    /*
    // DOM elements for view controls.
    var viewUpElement = document.querySelector('#viewUp');
    var viewDownElement = document.querySelector('#viewDown');
    var viewLeftElement = document.querySelector('#viewLeft');
    var viewRightElement = document.querySelector('#viewRight');
    var viewInElement = document.querySelector('#viewIn');
    var viewOutElement = document.querySelector('#viewOut');

    // Dynamic parameters for controls.
    var velocity = 0.7;
    var friction = 3;

    // Associate view controls with elements.
    var controls = viewer.controls();
    controls.registerMethod('upElement',    new Marzipano.ElementPressControlMethod(viewUpElement,     'y', -velocity, friction), true);
    controls.registerMethod('downElement',  new Marzipano.ElementPressControlMethod(viewDownElement,   'y',  velocity, friction), true);
    controls.registerMethod('leftElement',  new Marzipano.ElementPressControlMethod(viewLeftElement,   'x', -velocity, friction), true);
    controls.registerMethod('rightElement', new Marzipano.ElementPressControlMethod(viewRightElement,  'x',  velocity, friction), true);
    controls.registerMethod('inElement',    new Marzipano.ElementPressControlMethod(viewInElement,  'zoom', -velocity, friction), true);
    controls.registerMethod('outElement',   new Marzipano.ElementPressControlMethod(viewOutElement, 'zoom',  velocity, friction), true);
    */
}

export function changeSphereById(startSphereUUID) {
    const scene = findSceneById(startSphereUUID);
    if(scene) {
        switchScene(scene, null);
    }
}

export function getCurrentSceneUUID() {
    if(viewer.scene()) {
        for(const el of receivedScenes) {
            if(viewer.scene() === el.photosphere) {
                return el.uuid;
            }
        }
    }
    return null;
}

export function getCurrentScene() {
    if(viewer.scene()) {
        for(const el of receivedScenes) {
            if(viewer.scene() === el.photosphere) {
                return el;
            }
        }
    }
    return null;
}

export function getCurrentViewYaw() {
    const scene = viewer.scene();
    if (scene) {
        for(const el of receivedScenes) {
            if(scene === el.photosphere) {
                return ((scene.view().yaw() * 180 / Math.PI) + 360) % 360;
            }
        }
    }
    return null;
}

export function updateCurrentSceneMarkers(sceneYaw) {
    const scene = getCurrentScene();
    scene.yaw = sceneYaw;
    if(scene.uuid) {
        for(const el of receivedScenes) {
            if(el.uuid === scene.uuid) {
                var newMarkers = [];
                for(const marker of el.markers) {
                    el.photosphere.hotspotContainer().destroyHotspot(marker.hotspot);
                    if (marker.markerType === MARKER_TYPE_PHOTOSPHERE && marker.linkedPhotosphere) {
                        const element = createLinkHotspotElement(marker);
                        marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
                            element,
                            {
                                yaw: (((360 - el.yaw) + marker.yaw) % 360) * Math.PI / 180,
                                pitch: marker.pitch * Math.PI / 180
                            }
                        );
                    } else if (marker.markerType === MARKER_TYPE_PRODUCT) {
                        const element = createInfoHotspotElement(marker);
                        if (element != null) {
                            marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
                                element,
                                {
                                    yaw: (((360 - el.yaw) + marker.yaw) % 360) * Math.PI / 180,
                                    pitch: marker.pitch * Math.PI / 180
                                }
                            );
                        }
                    }else if (marker.markerType === MARKER_TYPE_NORTH) {
                        const element = createNorthHotspotElement(marker);
                        if (element != null) {
                            marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
                                element,
                                {
                                    yaw: (((360 - el.yaw) + marker.yaw) % 360) * Math.PI / 180,
                                    pitch: marker.pitch * Math.PI / 180
                                }
                            );
                        }
                    }

                    if(marker.hotspot) {
                        newMarkers.push(marker);
                    }
                }
                el.markers = newMarkers;
            }
        }
    }
}

export function setEditMode(editing) {
    editMode = editing;
    return editMode;
}

export function toggleEditMode() {
    editMode = !editMode;
    return editMode;
}

export function setMarkerEditListener(callback) {
    onMarkerEdit = callback;
}

export function setInfoMarkerOpenListener(callback) {
    onInfoMarkerOpen = callback;
}

export function setOnSceneChanged(callback) {
    onSceneChanged = callback;
}

export function setOnCreateProductMarker(callback) {
    onCreateProductMarker = callback;
}

export function setOnCreatePromotionMarker(callback) {
    onCreatePromotionMarker = callback;
}

export function deleteMarker(markerUUID) {
    const uuid = getCurrentSceneUUID();
    if(uuid) {
        for(const el of receivedScenes) {
            if(el.uuid === uuid) {
                let i = 0;
                for(const marker of el.markers) {
                    if(marker.uuid === markerUUID) {
                        el.photosphere.hotspotContainer().destroyHotspot(marker.hotspot);
                        el.markers.splice(i, 1);
                        return true;
                    }
                    i++;
                }
            }
        }
    }
    return false;
}

export function addMarker(marker, useMarkerPitch) {
    const ps = receivedScenes[findSceneIndexById(getCurrentSceneUUID())];
    if(marker.markerType === MARKER_TYPE_PHOTOSPHERE && marker.linkedPhotosphere) {
        const element = createLinkHotspotElement(marker);
        marker.hotspot = receivedScenes[findSceneIndexById(getCurrentSceneUUID())].photosphere.hotspotContainer().createHotspot(
            element,
            {
                // yaw: marker.yaw * Math.PI/180, Perchè non viene applicata la formula?? arrivava marker con yaw = a cameraview, verificare se si è rotto qualcosa
                yaw: (((360 - ps.yaw) + marker.yaw) % 360) * Math.PI / 180,
                pitch: useMarkerPitch ? marker.pitch * Math.PI/180 : 30 * Math.PI/180
            }
        );
    } else if(marker.markerType === MARKER_TYPE_PRODUCT || marker.markerType === MARKER_TYPE_PROMO) {
        const element = createInfoHotspotElement(marker, ps);
        if (element != null) {
            marker.hotspot = receivedScenes[findSceneIndexById(getCurrentSceneUUID())].photosphere.hotspotContainer().createHotspot(
                element,
                {
                    yaw: (marker.uuid.includes("TEMP")) ? marker.yaw * Math.PI / 180 : (((360 - ps.yaw) + marker.yaw) % 360) * Math.PI / 180,
                    pitch: marker.pitch * Math.PI / 180
                }
            );
        }
    }    else if(marker.markerType === MARKER_TYPE_NORTH) {
        const element = createNorthHotspotElement(marker);
        if (element != null) {
            marker.hotspot = receivedScenes[findSceneIndexById(getCurrentSceneUUID())].photosphere.hotspotContainer().createHotspot(
                element,
                {
                    yaw: (((360 - ps.yaw) + marker.yaw) % 360) * Math.PI / 180,
                    pitch: marker.pitch * Math.PI / 180
                }
            );
        }
    }
    if(marker.hotspot) {
        receivedScenes[findSceneIndexById(getCurrentSceneUUID())].markers.push(marker);
    }
    return marker;
}

export function getClickedMarkerYaw() {
    return clickedMarkerYaw;
}

export function createProductInfoHotspotElement(marker, scene) {
    const element = createInfoHotspotElement(marker);
    if (element != null) {
        marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
            element,
            {
                yaw: (((360 - scene.yaw) + marker.yaw) % 360) * Math.PI / 180,
                pitch: marker.pitch * Math.PI / 180
            }
        );
    }
}

function sanitize(s) {
    return s.replace('&', '&amp;').replace('<', '&lt;').replace('>', '&gt;');
}

function createScene(scene, createAdjacent) {
    if(!scene.photosphere && scene.media) {
        const source = Marzipano.ImageUrlSource.fromString(scene.media);
        const geometry = new Marzipano.EquirectGeometry([{width: 4096}]);

        scene.photosphere = viewer.createScene({
            source: source,
            geometry: geometry,
            view: view,
            pinFirstLevel: true
        });

        panoElement.addEventListener("mousedown", (e) => {
            startMousePosition = {
                x: e.x,
                y: e.y
            };
            clickAlreadyEmitted = false;
        });

        panoElement.addEventListener("mouseup", (e) => {
            if(startMousePosition.x > e.x - 5 &&
                startMousePosition.x < e.x + 5 &&
                startMousePosition.y > e.y - 5 &&
                startMousePosition.y < e.y + 5) {
                if(editMode && onSphereClick && !clickAlreadyEmitted) {
                    clickAlreadyEmitted = true;
                    onSphereClick(view.screenToCoordinates({x: e.offsetX, y: e.offsetY}));
                }
            }
        });

        if (scene.markers) {
            for (const marker of scene.markers) {
                if (marker.markerType === MARKER_TYPE_PHOTOSPHERE && marker.linkedPhotosphere) {
                    const element = createLinkHotspotElement(marker);
                    marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
                        element,
                        {
                            yaw: (((360 - scene.yaw) + marker.yaw) % 360) * Math.PI / 180,
                            pitch: marker.pitch * Math.PI / 180
                        }
                    );
                } else if (marker.markerType === MARKER_TYPE_PRODUCT || marker.markerType === MARKER_TYPE_PROMO) {
                    const element = createInfoHotspotElement(marker, scene);
                    if (element != null) {
                        marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
                            element,
                            {
                                yaw: (((360 - scene.yaw) + marker.yaw) % 360) * Math.PI / 180,
                                pitch: marker.pitch * Math.PI / 180
                            }
                        );
                    }
                } else if (marker.markerType === MARKER_TYPE_NORTH) {
                    const element = createNorthHotspotElement(marker, scene);
                    if (element != null) {
                        marker.hotspot = scene.photosphere.hotspotContainer().createHotspot(
                            element,
                            {
                                yaw: (((360 - scene.yaw) + marker.yaw) % 360) * Math.PI / 180,
                                pitch: marker.pitch * Math.PI / 180
                            }
                        );
                    }
                }
            }
        }
    }
    if (createAdjacent && scene.markers) {
        for (const marker of scene.markers) {
            if(marker.markerType === MARKER_TYPE_PHOTOSPHERE) {
                const linkedScene = findSceneById(marker.linkedPhotosphere);
                if(linkedScene) {
                    if(linkedScene.media) {
                        createScene(linkedScene, false);
                    } else {
                        onMediaRequest(linkedScene.uuid, false)
                    }
                }
            }
        }
    }
}

function switchScene(scene, yaw) {
    collapseAllMarkers();
    if(scene.media) {
        createScene(scene, true);
        setCurrentScene(scene, yaw);
    } else {
        if(onMediaRequest) {
            onMediaRequest(scene.uuid, true);
        }
    }
}

function setCurrentScene(scene, yaw) {
    clickedMarkerYaw = yaw;
    logData("clicked marker yaw", yaw);
    if (yaw != null) {
        yaw = findNearestMarkerYaw(scene.markers, yaw);
        scene.photosphere.view().setParameters({
            fov: 120 * Math.PI/180,
            yaw: (((360 - scene.yaw) + yaw) % 360) * Math.PI / 180
        });
    } else {
        scene.photosphere.view().setParameters({
            fov: 120 * Math.PI/180
        });
    }
    scene.photosphere.switchTo({
        transitionDuration: 0
    });
    onSceneChanged(scene);
    updateSceneName(scene);
    updateSceneList(scene);
}

function findNearestMarkerYaw(markers, yaw){
    var y = (yaw + 360) % 360;
    var min = 360;
    var defYaw = 0;
    markers.forEach((marker) => {
        if (marker.markerType === MARKER_TYPE_PHOTOSPHERE) {
            var x = (marker.yaw + 360) % 360;
            var diff = Math.min(360 - Math.abs(x - y), Math.abs(x - y));
            if (diff < min) {
                defYaw = marker.yaw;
                min = diff;
            }
        }
    });
    return defYaw;
}

export function onMediaReceived(uuid, media, changeScene) {
    const scene = findSceneById(uuid);
    if(scene) {
        scene.media = media;
        if(changeScene) {
            switchScene(scene, null);
        } else {
            createScene(scene, false);
        }
    }
}

export function setMediaRequestListener(listener) {
    onMediaRequest = listener;
}

function updateSceneName(scene) {
    sceneNameElement.innerHTML = sanitize(scene.name);
}

function updateSceneList(scene) {
    for (let i = 0; i < sceneElements.length; i++) {
        let el = sceneElements[i];
        if (el.getAttribute('data-id') === scene.uuid) {
            el.classList.add('current');
        } else {
            el.classList.remove('current');
        }
    }
}

function showSceneList() {
    sceneListElement.classList.add('enabled');
    sceneListToggleElement.classList.add('enabled');
}

function hideSceneList() {
    sceneListElement.classList.remove('enabled');
    sceneListToggleElement.classList.remove('enabled');
}

function toggleSceneList() {
    sceneListElement.classList.toggle('enabled');
    sceneListToggleElement.classList.toggle('enabled');
}

function createLinkHotspotElement(marker) {
    // Create wrapper element to hold icon and tooltip.
    let wrapper = document.createElement('div');
    if (findSceneById(marker.linkedPhotosphere)) {
        wrapper.id = marker.uuid;
        wrapper.classList.add('hotspot');
        wrapper.classList.add('link-hotspot');

        // Create image element.
        let icon = document.createElement('img');
        icon.src = 'assets/js/marzipano/img/link.png';
        icon.classList.add('link-hotspot-icon');

        wrapper.addEventListener('mouseup', function (e) {
            e.stopPropagation();
        }, true);

        if (!marker.enabled) {
            wrapper.classList.add('disabled-marker');
        }

        // Add click event handler.
        wrapper.addEventListener('click', function () {
            if (editMode) {
                if (onMarkerEdit) {
                    onMarkerEdit(marker.uuid);
                }
            } else {
                switchScene(findSceneById(marker.linkedPhotosphere), marker.yaw);
            }
        });

        // Prevent touch and scroll events from reaching the parent element.
        // This prevents the view control logic from interfering with the hotspot.
        stopTouchAndScrollEventPropagation(wrapper);

        // Create tooltip element.
        let tooltip = document.createElement('div');
        tooltip.classList.add('hotspot-tooltip');
        tooltip.classList.add('link-hotspot-tooltip');
        tooltip.innerHTML = findSceneById(marker.linkedPhotosphere).name;

        wrapper.appendChild(icon);
        wrapper.appendChild(tooltip);
    }

    return wrapper;
}

function createInfoHotspotElement(marker, scene) {
    if(marker.markerType === MARKER_TYPE_PRODUCT && marker.product != null && getProductName(marker.product) == null) {
        onCreateProductMarker(marker, scene);
        return;
    } else if (marker.markerType === MARKER_TYPE_PROMO && marker.promotionUuid != null && marker.promotion == null) {
        onCreatePromotionMarker(marker, scene);
        return;
    }

    // Create wrapper element to hold icon and tooltip.
    let wrapper = document.createElement('div');
    wrapper.id = marker.uuid;
    wrapper.classList.add('hotspot');
    if (marker.markerType === MARKER_TYPE_PROMO) {
        wrapper.classList.add('promo');
    }
    wrapper.classList.add('info-hotspot');

    // Create hotspot/tooltip header.
    let header = document.createElement('div');
    header.classList.add("info-hotspot-header");

    // Create image element.
    let iconWrapper = document.createElement('div');
    iconWrapper.classList.add("w-100");
    iconWrapper.classList.add('info-hotspot-icon-wrapper');
    iconWrapper.classList.add("align-self-center");

    let icon = document.createElement('img');
    if (marker.markerType === MARKER_TYPE_PROMO) {
        icon.src = 'assets/img/sell-white.svg';
    } else {
        icon.src = 'assets/img/nature-24px.svg';
    }
    icon.classList.add('info-hotspot-icon');
    icon.classList.add('pb-1');
    icon.classList.add("d-block");
    icon.classList.add("m-auto");
    iconWrapper.appendChild(icon);

    let pointer = document.createElement("div");
    pointer.classList.add("info-hotspot-pointer");

    if(marker.product && getProductName(marker.product)) {
        let spanName = document.createElement("span");
        spanName.classList.add('info-hotspot-name');
        spanName.classList.add("d-block");
        spanName.classList.add("m-auto");
        spanName.innerHTML = getProductName(marker.product);
        iconWrapper.appendChild(spanName);
    } else if (marker.markerType === MARKER_TYPE_PROMO && marker.promotion) {
        let spanName = document.createElement("span");
        spanName.classList.add('info-hotspot-name');
        spanName.classList.add("d-block");
        spanName.classList.add("m-auto");
        spanName.innerHTML = marker.promotion.title.toUpperCase();
        iconWrapper.appendChild(spanName);
    }

    // Construct header element.
    header.appendChild(iconWrapper);

    // Place header and text into wrapper element.
    wrapper.appendChild(header);

    wrapper.appendChild(pointer);

    wrapper.querySelector('.info-hotspot-header').addEventListener('mouseup', function(e) {
        e.stopPropagation();
    }, true);

    // Show content when hotspot is clicked.
    wrapper.querySelector('.info-hotspot-header').addEventListener('click', function() {
        if(editMode) {
            if(onMarkerEdit) {
                onMarkerEdit(marker.uuid);
            }
        } else {
            if(onInfoMarkerOpen && !wrapper.classList.contains("expanded")) {
                onInfoMarkerOpen(marker.uuid);
            }
        }
    });

    // Prevent touch and scroll events from reaching the parent element.
    // This prevents the view control logic from interfering with the hotspot.
    stopTouchAndScrollEventPropagation(wrapper);

    return wrapper;
}

function createNorthHotspotElement(marker, scene) {
    // Create wrapper element to hold icon and tooltip.
    let wrapper = document.createElement('div');
    wrapper.id = marker.uuid;
    wrapper.classList.add('hotspot');
    wrapper.classList.add('link-hotspot');

    // Create image element.
    let icon = document.createElement('img');
    icon.src = 'assets/img/north-direction.png';
    icon.classList.add('link-hotspot-icon');

    wrapper.addEventListener('mouseup', function (e) {
        e.stopPropagation();
    }, true);

    if (!marker.enabled) {
        wrapper.classList.add('disabled-marker');
    }

    // Prevent touch and scroll events from reaching the parent element.
    // This prevents the view control logic from interfering with the hotspot.
    stopTouchAndScrollEventPropagation(wrapper);

    // Create tooltip element.

    wrapper.appendChild(icon);
    return wrapper;
}

export function expandInfoMarker(markerUUID, imgURL, productInfo) {
    const scene = getCurrentScene();
    if (scene && scene.markers) {
        scene.markers.forEach((marker) => {
            if(marker.uuid === markerUUID) {
                let element = document.getElementById(marker.uuid);
                element.classList.add("expanded");
                element.classList.add("expanded-margins");
                let icon = element.getElementsByClassName("info-hotspot-icon")[0];
                icon.src = imgURL;
                let span = element.getElementsByClassName("info-hotspot-name")[0];
                span.innerHTML = productInfo;
                collapseOnClickOutside(element, markerUUID);
            }
        });
    }
}

function collapseInfoMarker(markerUUID) {
    const scene = getCurrentScene();
    if (scene && scene.markers) {
        scene.markers.forEach((marker) => {
            if (marker.uuid === markerUUID) {
                let element = document.getElementById(marker.uuid);
                element.classList.remove("expanded");
                element.classList.remove("expanded-margins");
                let icon = element.getElementsByClassName("info-hotspot-icon")[0];
                let span = element.getElementsByClassName("info-hotspot-name")[0];
                if (marker.markerType === MARKER_TYPE_PROMO) {
                    icon.src = 'assets/img/sell-white.svg';
                    span.innerHTML = marker.promotion ? marker.promotion.title.toUpperCase() : '';
                } else {
                    icon.src = 'assets/img/nature-24px.svg';
                    span.innerHTML = getProductName(marker.product);
                }
            }
        });
    }
}

function collapseOnClickOutside(markerEl, markerUUID) {
    const outsideClickListener = event => {
        if (!markerEl.contains(event.target)) {
            collapseInfoMarker(markerUUID);
            removeClickListener()
        }
    };

    const removeClickListener = () => {
        document.removeEventListener('click', outsideClickListener)
    };

    document.addEventListener('click', outsideClickListener)
}

function collapseAllMarkers() {
    const els = document.getElementsByClassName("info-hotspot");
    if(els) {
        for(const element of els) {
            //collapseInfoMarker(element.id);
        }
    }
}

export function setLinkMarkerEnabled(uuid, enabled) {
    const scene = getCurrentScene();
    if(scene.markers) {
        scene.markers.forEach((marker) => {
            if(marker.uuid === uuid) {
                marker.enabled = enabled;
                const el = document.getElementById(uuid);
                if(el) {
                    if(!enabled) {
                        el.classList.add("disabled-marker");
                    } else {
                        el.classList.remove("disabled-marker");
                    }
                }
            }
        });
    }
}

// Prevent touch and scroll events from reaching the parent element.
function stopTouchAndScrollEventPropagation(element, eventList) {
    var eventList = [ 'touchstart', 'touchmove', 'touchend', 'touchcancel',
        'wheel', 'mousewheel' ];
    for (let i = 0; i < eventList.length; i++) {
        element.addEventListener(eventList[i], function(event) {
            event.stopPropagation();
        });
    }
}

function findSceneById(id) {
    for (let i = 0; i < receivedScenes.length; i++) {
        if (receivedScenes[i].uuid === id) {
            return receivedScenes[i];
        }
    }
    return null;
}

function findSceneIndexById(id) {
    for (let i = 0; i < receivedScenes.length; i++) {
        if (receivedScenes[i].uuid === id) {
            return i;
        }
    }
    return null;
}

function getProductName(productModel) {
    const lang = localStorage.getItem(LOCAL_STORAGE_CURRENT_LANG);
    switch (lang) {
        case LANG_CODE_IT:
            return productModel.varDex;
        case LANG_CODE_EN:
            return productModel.varIng !== undefined && productModel.varIng !== null
            && productModel.varIng.length !== 0 ? productModel.varIng : productModel.varDex;
        case LANG_CODE_FR:
            return productModel.varFra !== undefined && productModel.varFra !== null
            && productModel.varFra.length !== 0 ? productModel.varFra : productModel.varDex;
    }
}
